import React, { Component } from 'react'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import moment from 'moment'
import { Grid, Row, Col } from 'react-flexbox-grid'
import { Button, Table, TableHeader, TableLoading, Colors } from 'advenio-components'
import DocumentsActions from '../../../Data/Redux/DocumentsRedux'

/* ------------- Styled Components ------------- */

const BackButton = styled.div`
  margin-top: 15px;
`

const BackLink = styled.span`
  color: ${Colors.blue500};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  &:hover {
    text-decoration: underline;
  }
`

const HeaderCard = styled.div`
  background: ${Colors.blue500};
  border-radius: 5px;
  padding: 20px 25px;
  margin-top: 15px;
  color: #FFFFFF;
`

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const TitleSection = styled.div`
  flex: 1;
`

const Title = styled.h1`
  margin: 0 0 8px 0;
  font-size: 22px;
  font-weight: 700;
  color: #FFFFFF;
`

const Subtitle = styled.p`
  margin: 0 0 16px 0;
  color: #FFFFFF;
  opacity: 0.8;
  font-size: 14px;
`

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
`

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
`

const MetaLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  opacity: 0.7;
  margin-bottom: 2px;
  color: #FFFFFF;
`

const MetaValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #FFFFFF;
`

const TabsContainer = styled.div`
  display: flex;
  margin-top: 20px;
  border-bottom: 2px solid ${Colors.gray200};
`

const Tab = styled.div`
  padding: 12px 24px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: ${function (props) { return props.active ? Colors.blue500 : '#637381' }};
  border-bottom: 2px solid ${function (props) { return props.active ? Colors.blue500 : 'transparent' }};
  margin-bottom: -2px;
  transition: all 0.2s ease;
  &:hover {
    color: ${Colors.blue500};
  }
`

const TableContainer = styled.div`
  position: relative;
  margin-top: 15px;
`

const CellContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  white-space: normal;
  height: 55px;
  color: #637381;
  font-size: 14px;
  background-color: ${function (props) { return props.index % 2 ? Colors.gray200 : Colors.white300 }};
`

const LeftCell = styled(CellContainer)`
  justify-content: flex-start;
  padding-left: 14px;
`

const NoResults = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 120px;
  color: #637381;
  font-size: 14px;
  border: 1px solid ${Colors.gray200};
  border-radius: 5px;
  margin-top: 15px;
`

/* ------------- Component ------------- */

var TABS = {
  HISTORY: 'history',
  VERSIONS: 'versions'
}

class DocumentDetail extends Component {
  constructor (props) {
    super(props)
    this.state = {
      activeTab: TABS.VERSIONS,
      document: null
    }
  }

  componentDidMount () {
    var documentId = this.props.match.params.id
    this.props.fetchVersions(documentId)
    this.props.fetchHistory(documentId)

    // Find document from library list
    var doc = this.findDocument(documentId)
    if (doc) {
      this.setState({ document: doc })
    } else {
      // If not in state (e.g. page refresh), fetch documents list
      this.props.fetchDocuments({})
    }
  }

  componentDidUpdate (prevProps) {
    if (prevProps.libraryDocuments !== this.props.libraryDocuments && !this.state.document) {
      var documentId = this.props.match.params.id
      var doc = this.findDocument(documentId)
      if (doc) {
        this.setState({ document: doc })
      }
    }
  }

  findDocument = (documentId) => {
    var docs = this.props.libraryDocuments
    for (var i = 0; i < docs.length; i++) {
      if (String(docs[i].id) === String(documentId)) {
        return docs[i]
      }
    }
    return null
  }

  goBack = () => {
    this.props.goTo('/documents')
  }

  handleTabChange = (tab) => {
    this.setState({ activeTab: tab })
  }

  formatFileSize = (bytes) => {
    if (!bytes) return '0 B'
    var sizes = ['B', 'KB', 'MB', 'GB']
    var i = Math.floor(Math.log(bytes) / Math.log(1024))
    return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + sizes[i]
  }

  formatMimeType = (mime) => {
    if (!mime) return '-'
    var map = {
      'application/pdf': 'PDF',
      'application/msword': 'Word',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word (DOCX)',
      'application/vnd.ms-excel': 'Excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel (XLSX)',
      'application/vnd.ms-powerpoint': 'PowerPoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint (PPTX)',
      'image/jpeg': 'Imagen (JPG)',
      'image/png': 'Imagen (PNG)',
      'text/plain': 'Texto'
    }
    return map[mime] || mime
  }

  handleDownload = (versionId) => {
    var documentId = this.props.match.params.id
    this.props.downloadVersion(documentId, versionId)
  }

  renderHeader = () => {
    var doc = this.state.document
    if (!doc) return null

    // Use latest version data if available
    var documentId = this.props.match.params.id
    var versions = this.props.versionsData[documentId] || []
    var latestVersion = versions.length > 0 ? versions[0] : null
    var fileSize = latestVersion ? latestVersion.file_size : doc.file_size

    return (
      <HeaderCard>
        <HeaderContent>
          <TitleSection>
            <Title>{doc.name}</Title>
          </TitleSection>
        </HeaderContent>
        <MetaRow>
          <MetaItem>
            <MetaLabel>Tipo</MetaLabel>
            <MetaValue>{this.formatMimeType(doc.mime_type)}</MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>Tamaño (última versión)</MetaLabel>
            <MetaValue>{this.formatFileSize(fileSize)}</MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>Versiones</MetaLabel>
            <MetaValue>{versions.length || doc.version_count || 1}</MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>Fecha de creación</MetaLabel>
            <MetaValue>
              {doc.created_at ? moment(doc.created_at).format('DD-MM-YYYY') : '-'}
            </MetaValue>
          </MetaItem>
        </MetaRow>
      </HeaderCard>
    )
  }

  renderHistory = () => {
    var documentId = this.props.match.params.id
    var data = this.props.historyData[documentId] || []
    var fetching = this.props.fetchingHistory

    if (fetching) {
      return (
        <TableContainer>
          <TableLoading
            roundedCorners
            columns={[
              { id: 1, header: 'Versión', element: 'Text', width: '80px', center: true },
              { id: 2, header: 'Enviado por', element: 'Text' },
              { id: 3, header: 'Fecha', element: 'Text', width: '130px', center: true },
              { id: 4, header: 'Estudiantes', element: 'Text', width: '120px', center: true },
            ]}
            rows={3}
          />
        </TableContainer>
      )
    }

    if (!data || data.length === 0) {
      return <NoResults>No hay historial de distribución disponible</NoResults>
    }

    return (
      <TableContainer>
        <Table
          curvedBottom
          withBorder
          roundedCorners
          data={data}
          columns={[
            {
              Header: function () { return <TableHeader blue title='Versión' /> },
              id: 'version',
              accessor: function (row) {
                return row.version ? 'v' + row.version.version_number : '-'
              },
              width: 120,
              Cell: function (props) {
                return <CellContainer index={props.index}>{props.value}</CellContainer>
              }
            },
            {
              Header: function () { return <TableHeader blue title='Enviado por' /> },
              id: 'sent_by',
              accessor: function (row) {
                if (!row.sent_by) return '-'
                return row.sent_by.name || row.sent_by.email || 'Usuario #' + row.sent_by.id
              },
              Cell: function (props) {
                return <LeftCell index={props.index}>{props.value}</LeftCell>
              }
            },
            {
              Header: function () { return <TableHeader blue center title='Fecha' /> },
              id: 'sent_at',
              accessor: 'sent_at',
              width: 130,
              Cell: function (props) {
                var val = props.value
                if (!val) return <CellContainer index={props.index}>-</CellContainer>
                var dateStr = (val && val.date) ? val.date : val
                var parsed = moment(dateStr)
                return <CellContainer index={props.index}>{parsed.isValid() ? parsed.format('DD-MM-YYYY') : '-'}</CellContainer>
              }
            },
            {
              Header: function () { return <TableHeader blue center title='Estudiantes' /> },
              id: 'student_count',
              accessor: 'student_count',
              width: 120,
              Cell: function (props) {
                return <CellContainer index={props.index}>{props.value || 0}</CellContainer>
              }
            }
          ]}
        />
      </TableContainer>
    )
  }

  renderVersions = () => {
    var documentId = this.props.match.params.id
    var data = this.props.versionsData[documentId] || []
    var fetching = this.props.fetchingVersions
    var self = this

    if (fetching) {
      return (
        <TableContainer>
          <TableLoading
            roundedCorners
            columns={[
              { id: 1, header: 'Versión', element: 'Text', width: '90px', center: true },
              { id: 2, header: 'Notas', element: 'Text' },
              { id: 3, header: 'Tipo', element: 'Text', width: '120px', center: true },
              { id: 4, header: 'Tamaño', element: 'Text', width: '100px', center: true },
              { id: 5, header: 'Subido por', element: 'Text', width: '160px' },
              { id: 6, header: 'Fecha', element: 'Text', width: '130px', center: true },
              { id: 7, header: '', element: 'Text', width: '110px' },
            ]}
            rows={3}
          />
        </TableContainer>
      )
    }

    if (!data || data.length === 0) {
      return <NoResults>No hay versiones disponibles</NoResults>
    }

    return (
      <TableContainer>
        <Table
          curvedBottom
          withBorder
          roundedCorners
          data={data}
          columns={[
            {
              Header: function () { return <TableHeader blue title='Versión' /> },
              id: 'version_number',
              accessor: 'version_number',
              width: 90,
              Cell: function (props) {
                return <CellContainer index={props.index}>v{props.value}</CellContainer>
              }
            },
            {
              Header: function () { return <TableHeader blue title='Notas' /> },
              id: 'notes',
              accessor: 'notes',
              Cell: function (props) {
                return <LeftCell index={props.index}>{props.value || '-'}</LeftCell>
              }
            },
            {
              Header: function () { return <TableHeader blue center title='Tipo' /> },
              id: 'mime_type',
              accessor: 'mime_type',
              width: 120,
              Cell: function (props) {
                return <CellContainer index={props.index}>{self.formatMimeType(props.value)}</CellContainer>
              }
            },
            {
              Header: function () { return <TableHeader blue center title='Tamaño' /> },
              id: 'file_size',
              accessor: 'file_size',
              width: 100,
              Cell: function (props) {
                return <CellContainer index={props.index}>{self.formatFileSize(props.value)}</CellContainer>
              }
            },
            {
              Header: function () { return <TableHeader blue title='Subido por' /> },
              id: 'uploaded_by',
              accessor: 'uploaded_by',
              width: 160,
              Cell: function (props) {
                var val = props.value
                var name = '-'
                if (val) {
                  name = val.name || val.email || '-'
                }
                return <LeftCell index={props.index}>{name}</LeftCell>
              }
            },
            {
              Header: function () { return <TableHeader blue center title='Fecha' /> },
              id: 'created_at',
              accessor: 'created_at',
              width: 130,
              Cell: function (props) {
                var val = props.value
                if (!val) return <CellContainer index={props.index}>-</CellContainer>
                // Handle Carbon object {date: "...", timezone_type: 3, timezone: "..."} or ISO string
                var dateStr = (val && val.date) ? val.date : val
                var parsed = moment(dateStr)
                return (
                  <CellContainer index={props.index}>
                    {parsed.isValid() ? parsed.format('DD-MM-YYYY') : '-'}
                  </CellContainer>
                )
              }
            },
            {
              Header: function () { return <TableHeader blue /> },
              id: 'download',
              accessor: 'id',
              width: 110,
              Cell: function (props) {
                return (
                  <CellContainer index={props.index}>
                    <Button
                      default
                      small
                      onClick={function () { self.handleDownload(props.value) }}
                    >
                      Descargar
                    </Button>
                  </CellContainer>
                )
              }
            }
          ]}
        />
      </TableContainer>
    )
  }

  render () {
    var activeTab = this.state.activeTab

    return (
      <Grid fluid>
        <Row>
          <Col xs={12}>
            <BackButton>
              <Button onClick={this.goBack}>
                 <img style={{ marginRight: '10px' }} src="/assets/back.svg" alt="" />
                  <span>Regresar</span>
              </Button>
            </BackButton>
            {this.renderHeader()}
            <TabsContainer>
              <Tab
                active={activeTab === TABS.VERSIONS}
                onClick={this.handleTabChange.bind(this, TABS.VERSIONS)}
              >
                Versiones
              </Tab>
              <Tab
                active={activeTab === TABS.HISTORY}
                onClick={this.handleTabChange.bind(this, TABS.HISTORY)}
              >
                Historial de Distribución
              </Tab>
            </TabsContainer>
            {activeTab === TABS.VERSIONS && this.renderVersions()}
            {activeTab === TABS.HISTORY && this.renderHistory()}
          </Col>
        </Row>
      </Grid>
    )
  }
}

DocumentDetail.propTypes = {
  match: PropTypes.object.isRequired,
  libraryDocuments: PropTypes.array,
  versionsData: PropTypes.object,
  historyData: PropTypes.object,
  fetchingVersions: PropTypes.bool,
  fetchingHistory: PropTypes.bool,
  fetchDocuments: PropTypes.func.isRequired,
  fetchVersions: PropTypes.func.isRequired,
  fetchHistory: PropTypes.func.isRequired,
  goTo: PropTypes.func.isRequired
}

const mapStateToProps = ({ documents }) => ({
  libraryDocuments: documents.library.documents,
  versionsData: documents.versions.data,
  historyData: documents.history.data,
  fetchingVersions: documents.versions.fetching,
  fetchingHistory: documents.history.fetching
})

const mapDispatchToProps = dispatch => ({
  fetchDocuments: (query) => dispatch(DocumentsActions.fetchDocumentsRequest(query)),
  fetchVersions: (documentId) => dispatch(DocumentsActions.fetchDocumentVersionsRequest(documentId)),
  fetchHistory: (documentId) => dispatch(DocumentsActions.fetchDistributionHistoryRequest(documentId)),
  downloadVersion: (documentId, versionId) => dispatch(DocumentsActions.downloadVersionRequest(documentId, versionId)),
  goTo: (path) => dispatch(push(path))
})

export default connect(mapStateToProps, mapDispatchToProps)(DocumentDetail)



// WEBPACK FOOTER //
// ./src/Containers/Documents/DocumentDetail/index.jsx